import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import "./AddEventModal.css";
import Select from "react-select";
import apiServiceHandler from "../../service/apiService";
import "toastr/build/toastr.min.css";
import toastr from "toastr";

const groupOptions = [
  { value: "stg90", label: "STG Group" },
  { value: "boa", label: "LAB Group" },
  { value: "club84", label: "CCA Group" },
];

const repeatOptions = [
  { value: "once", label: "Once" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
];

const durationOptions = [
  { value: 30, label: "30 minutes" },
  { value: 60, label: "1 hour" },
  { value: 90, label: "1 hour 30 minutes" },
  { value: 120, label: "2 hours" },
  { value: "custom", label: "Custom" },
];

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    boxShadow: 'none',
    border: state.isFocused ? '1px solid #ccc' : '1px solid #ddd',
    outline: 'none',
    '&:hover': {
      border: '1px solid #aaa',
    },
  })
};

const convertUtcToLocalDatetime = (utcDate) => {
  const date = new Date(utcDate);
  const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  return localDate.toISOString().slice(0, 16);
};


const EditEventModal = ({ isOpen, onClose, setRefreshEventData, event }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [meetingURL, setMeetingURL] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [repeatSelectedOption, setRepeatSelectedOption] = useState(null);
  const [groupSelectedOption, setGroupSelectedOption] = useState(null);
  const [durationSelectedOption, setDurationSelectedOption] = useState(null);
  const [showCustomEndDate, setShowCustomEndDate] = useState(false);

  // console.log(event, 'Edit event')

  useEffect(() => {
    if (event && event.id) {
      const durationInMinutes = (new Date(event.end) - new Date(event.start)) / (1000 * 60);
      const matchedOption = durationOptions.find(option => option.value === durationInMinutes);
      setTitle(event.title || "");
      setDescription(event.description || "");
      setMeetingURL(event.meeting_url || "");
      setStartDate(convertUtcToLocalDatetime(event.start));
      setEndDate(convertUtcToLocalDatetime(event.end));
      setRepeatSelectedOption(repeatOptions.find(option => option.value === event.repeat) || null);
      setGroupSelectedOption(groupOptions.find(option => option.value === event.group_code) || null);
      setDurationSelectedOption(matchedOption || durationOptions.find(option => option.value === "custom"));
    }
  }, [event]);

  const handleDurationChange = (option) => {
    setDurationSelectedOption(option);
    if (option.value === "custom") {
      setShowCustomEndDate(true);
      setEndDate("");
    } else {
      setShowCustomEndDate(false);
      if (startDate) {
        const startDateTime = new Date(startDate);
        const calculatedEndDate = new Date(startDateTime.getTime() + option.value * 60000);
        setEndDate(calculatedEndDate);
      }
    }
  };

  const sendEditEvent = async () => {
    if (!(title && startDate && endDate && repeatSelectedOption && groupSelectedOption)) {
      toastr.error("Please fill in all fields");
      return;
    }

    const payload = {
      title,
      description,
      meeting_url: meetingURL,
      start: new Date(startDate).toUTCString(),
      end: new Date(endDate).toUTCString(),
      repeat: repeatSelectedOption.value,
      group_code: groupSelectedOption.value
    };

    try {
      const response = await apiServiceHandler("PATCH", `api/calendar/events/${event.id}`, payload);
      if (response.auth) {
        toastr.success(response.message);
        setRefreshEventData(true);
        onClose();
      } else {
        toastr.error(response.message || "Failed to edit event. Please try again.");
      }
    } catch (error) {
      toastr.error(error.response?.data?.message || "An error occurred.");
      console.error(error);
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} centered>
      <Modal.Header closeButton style={{ borderBottom: "none" }}>
        <Modal.Title>Edit Event</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="invite-form__fields">
          <div className="invite-form__field">
            <div className="text-input__label">Title</div>
            <div className="form-input__wrapper">
              <input
                type="text"
                className="form-input__input"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
          </div>
          <div className="invite-form__field">
            <div className="text-input__label">Description</div>
            <div className="form-input__wrapper">
              <input
                type="text"
                className="form-input__input"
                required
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
          <div className="invite-form__field">
            <div className="text-input__label">Meeting Link</div>
            <div className="form-input__wrapper">
              <input
                type="text"
                className="form-input__input"
                required
                value={meetingURL}
                onChange={(e) => setMeetingURL(e.target.value)}
              />
            </div>
          </div>
          <div className="invite-form__field">
            <div className="text-input__label">Start Date</div>
            <div className="form-input__wrapper">
              <input
                type="datetime-local"
                className="form-input__input"
                required
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
          </div>
          <div className="select-invite-form__field">
            <div className="select__label">Duration</div>
            <Select
              value={durationSelectedOption}
              onChange={handleDurationChange}
              options={durationOptions}
              styles={customStyles}
            />
          </div>
          {showCustomEndDate && <div className="invite-form__field">
            <div className="text-input__label">End Date</div>
            <div className="form-input__wrapper">
              <input
                type="datetime-local"
                className="form-input__input"
                required
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>}
          <div className="select-invite-form__field">
            <div className="select__label">Repeat</div>
            <Select
              value={repeatSelectedOption}
              onChange={setRepeatSelectedOption}
              options={repeatOptions}
              styles={customStyles}
            />
          </div>
          <div className="select-invite-form__field">
            <div className="select__label">Select Group</div>
            <Select
              value={groupSelectedOption}
              onChange={setGroupSelectedOption}
              options={groupOptions}
              styles={customStyles}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: "none", paddingTop: '1rem' }}>
        <button className="send-invite__btn" onClick={sendEditEvent}>
          <span className="send-invite-button__label">Save Changes</span>
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditEventModal;
